import React from "react";
import { Link } from "react-router-dom";
import {
  Library, Trophy, ClipboardList, Copy as CopyIcon, Award, GitCompareArrows,
  Sparkles, MapPin, ArrowRight, ShieldCheck,
} from "lucide-react";
import CollectorProfilePreview from "@/components/CollectorProfilePreview";
import { GIL_COLLECTOR_PROFILE } from "@/data/collectorProfilePreview";

/**
 * FutureCollectorNetwork
 * ──────────────────────
 * A visual preview of upcoming collector-profile / trading-network features.
 * Every card and preview line is explicitly labeled as COMING SOON / IN
 * DEVELOPMENT so visitors are never misled about what's live today.
 *
 * All wording emphasizes that the future platform is for a collector's
 * ENTIRE World Cup collection — across every tournament — not just 2026.
 *
 * The concept collector profile (Gil's approved example) is rendered by the
 * shared `<CollectorProfilePreview />` component driven by
 * `/app/frontend/src/data/collectorProfilePreview.js`.
 */

// ─── Feature grid used on the Home page ──────────────────────────────
const FEATURE_ICONS = [
  { Icon: Library,          label: "Track Your Entire Collection from Previous World Cups" },
  { Icon: Trophy,           label: "Completion Progress Across Every Tournament" },
  { Icon: ClipboardList,    label: "Missing Stickers from Previous World Cups" },
  { Icon: CopyIcon,         label: "Duplicates from Previous World Cups" },
  { Icon: Award,            label: "Collector Badges & Levels" },
  { Icon: ShieldCheck,      label: "Verified Collector Status" },
  { Icon: GitCompareArrows, label: "Smart Trade Matching" },
  { Icon: MapPin,           label: "Nearby Collectors" },
];

// Concept-only trade matching examples come from the shared profile data
// so the same lines can be reused across the site.
const TRADE_EXAMPLES = GIL_COLLECTOR_PROFILE.tradeRecommendations;

// ─── Signup expandable groups ────────────────────────────────────────
const SIGNUP_GROUPS = [
  { title: "Show Your Entire World Cup Collection", items: [
    "Add albums from every World Cup you own",
    "Display completed albums from previous tournaments",
    "Track albums still in progress",
    "Show completion percentages for every album",
    "Build a shareable collector profile that spans every tournament",
  ]},
  { title: "Track What You Still Need", items: [
    "Save missing stickers from previous World Cup albums",
    "Organize lists by tournament and album",
    "Update progress as albums become more complete",
  ]},
  { title: "Show the Duplicates You Have", items: [
    "Add duplicate stickers from every World Cup you've collected",
    "Make duplicates visible to other collectors",
    "Help collectors discover possible trades across tournaments",
  ]},
  { title: "Connect and Trade Across Tournaments", items: [
    "Search collector profiles",
    "Compare missing and duplicate lists across every World Cup",
    "Find collectors near you",
    "Message collectors about possible trades",
    "Receive recommendations based on compatible collections",
  ]},
  { title: "Build Your Collector Status", items: [
    "Earn badges for albums from previous World Cups",
    "Display verified completion certificates",
    "Increase your collector level or tier",
    "Create a long-term record of your entire World Cup collection",
  ]},
];

// ═════════════════════════════════════════════════════════════════════
// HOMEPAGE variant
// ═════════════════════════════════════════════════════════════════════
export function FutureNetworkHome() {
  return (
    <section className="container-gm py-16 md:py-24" data-testid="home-future-network">
      <div className="max-w-3xl">
        <div className="kicker kicker-red">The Future of Sticker Collecting</div>
        <h2 className="h-display text-3xl md:text-6xl mt-3 leading-tight">
          Your Entire World Cup Collection.<br />
          <span className="text-[var(--panini-red)]">One Collector Profile.</span>
        </h2>
        <div className="mt-5 inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-yellow)] px-4 py-1.5 text-xs font-bold uppercase tracking-widest">
          <Sparkles size={14} /> Collector Network In Development
        </div>
        <p className="mt-5 text-[var(--smoke)] text-sm md:text-base leading-relaxed max-w-2xl">
          One profile for every World Cup album you own — from previous
          tournaments through the upcoming USA / Canada / Mexico 2026 edition.
          Track completions, list your missing stickers, show the duplicates
          you can trade, and connect with collectors who need exactly what you
          have.
        </p>
      </div>

      {/* Two-column: profile mockup + feature icon cards */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-8 mt-10 items-start">
        <CollectorProfilePreview />
        <FeatureIconGrid />
      </div>

      {/* Trade preview strip */}
      <div className="mt-10 rounded-3xl border-4 border-[var(--ink)] bg-[var(--field-green)] p-6 md:p-8 shadow-[8px_8px_0_var(--panini-yellow)] text-white" data-testid="future-trade-callout">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-display text-xl md:text-2xl">Stop searching endless group posts.</h3>
          <span className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 bg-white/10 backdrop-blur-sm px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
            <Sparkles size={12} /> Future Trade Matching Preview
          </span>
        </div>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          {TRADE_EXAMPLES.map((line, i) => (
            <div key={i}
                 className="rounded-2xl border-2 border-white/30 bg-white/10 backdrop-blur-sm px-4 py-3 flex items-start gap-2 text-sm"
                 data-testid={`future-trade-example-${i + 1}`}>
              <GitCompareArrows size={16} className="mt-0.5 flex-shrink-0 text-[var(--panini-yellow)]" />
              <span>{line}</span>
            </div>
          ))}
        </div>
        <p className="text-white/60 text-[10px] uppercase tracking-widest mt-4">
          Concept preview only. Not real users.
        </p>
      </div>

      {/* CTA */}
      <div className="mt-10 rounded-3xl border-4 border-[var(--ink)] bg-[var(--panini-yellow)] p-7 md:p-10 shadow-[10px_10px_0_var(--panini-red)] text-center" data-testid="future-account-cta">
        <h3 className="font-display text-2xl md:text-4xl leading-tight max-w-xl mx-auto">
          Create Your Collector Identity.
        </h3>
        <p className="text-[var(--ink)]/80 mt-4 max-w-xl mx-auto leading-relaxed">
          Create your collector account today. As new features are released,
          your profile will automatically grow into your permanent World Cup
          collector identity.
        </p>
        <div className="mt-6 flex flex-col md:flex-row items-stretch md:items-center justify-center gap-3">
          <Link to="/register"
                data-testid="future-cta-register"
                className="btn-red inline-flex items-center justify-center gap-2 min-h-[52px] px-6 md:px-8 py-3 text-base">
            Create My Collector Account <ArrowRight size={18} />
          </Link>
          <Link to="/registry"
                data-testid="future-cta-registry"
                className="inline-flex items-center justify-center gap-2 min-h-[52px] px-6 md:px-8 py-3 text-base rounded-full border-2 border-[var(--ink)] bg-white text-[var(--ink)] font-display hover:bg-[var(--ink)] hover:text-white transition-colors">
            View the Collector Registry
          </Link>
        </div>
      </div>
    </section>
  );
}

// Export the compact feature grid so Register.jsx can reuse it
export function FeatureIconGrid({ compact = false }) {
  const items = compact ? FEATURE_ICONS.slice(0, 6) : FEATURE_ICONS;
  const cols = compact ? "grid-cols-2 md:grid-cols-3" : "grid-cols-2";
  return (
    <div className={`grid ${cols} gap-3`} data-testid="future-feature-grid">
      {items.map((c, i) => (
        <div
          key={c.label}
          className="relative rounded-2xl border-2 border-[var(--ink)] bg-white p-4 shadow-[4px_4px_0_var(--panini-yellow)]"
          data-testid={`future-feature-${i + 1}`}
        >
          <div className="absolute -top-2 right-3 rounded-full bg-[var(--ink)] text-white px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest">
            Coming Soon
          </div>
          <div className="w-10 h-10 rounded-xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] flex items-center justify-center">
            <c.Icon size={18} className="text-[var(--ink)]" />
          </div>
          <div className="font-display text-sm mt-3 leading-tight">{c.label}</div>
        </div>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// CERTIFICATE DETAIL variant (unchanged behavior)
// ═════════════════════════════════════════════════════════════════════
const CERT_NOW = [
  "Create your permanent collector identity",
  "Claim verified certificates",
  "Keep certificates connected to your account",
  "Begin building your public collector presence",
];
const CERT_LATER = [
  "Display every World Cup album you own",
  "Mark albums complete or in progress",
  "Show album completion percentages",
  "Add missing sticker lists from previous World Cups",
  "Add duplicate sticker lists from previous World Cups",
  "Earn album badges",
  "Build your collector tier",
  "Discover collectors with matching trade opportunities",
  "Find collectors near you",
  "Receive personalized sticker-match recommendations",
];

export function FutureNetworkCertificate() {
  return (
    <div className="mt-14" data-testid="cert-future-network">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-widest text-white/60">
          <Sparkles size={14} /> Claim More Than a Certificate
        </div>
        <h3 className="font-serif text-2xl md:text-3xl mt-4 text-white">
          Claiming builds your future collector profile.
        </h3>
        <p className="text-white/60 mt-3 max-w-xl mx-auto text-sm md:text-base leading-relaxed">
          Claiming connects this verified certificate to your permanent
          collector profile — the future home of your entire World Cup
          collection across every tournament.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
        <BenefitPanel title="Currently Available"
                     tone="gold"
                     items={CERT_NOW}
                     testid="cert-benefits-now" />
        <BenefitPanel title="Coming Soon"
                     tone="ghost"
                     items={CERT_LATER}
                     testid="cert-benefits-later"
                     labelSuffix="in development" />
      </div>

      <div className="mt-8 rounded-3xl border border-[var(--gm-gold)]/30 bg-white/[0.03] p-7 md:p-9" data-testid="cert-profile-preview">
        <div className="flex items-center gap-2 text-[var(--gm-gold)] text-xs uppercase tracking-widest">
          <Sparkles size={14} /> Profile Features In Development
        </div>
        <h4 className="font-serif text-xl mt-3 text-white">A glimpse of the future collector profile</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5" data-testid="cert-profile-fields">
          {[
            "Verified Certificates", "Albums Owned", "Completed Albums",
            "Albums in Progress", "Missing Stickers", "Available Duplicates",
            "Collector Badges", "Collector Tier", "Trade Matches",
          ].map((label) => (
            <div key={label}
                 className="rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-center"
                 data-testid={`cert-profile-field-${label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="text-white/40 text-[10px] uppercase tracking-widest">Placeholder</div>
              <div className="text-white/80 text-sm mt-1">{label}</div>
            </div>
          ))}
        </div>
        <p className="text-white/40 text-xs mt-5">
          Neutral placeholders shown. No live counts or invented data.
        </p>
      </div>
    </div>
  );
}

function BenefitPanel({ title, items, tone, testid, labelSuffix }) {
  const gold = tone === "gold";
  return (
    <div className={`rounded-3xl border p-6 md:p-7 ${
      gold ? "border-[var(--gm-gold)]/40 bg-[var(--gm-gold)]/8"
           : "border-white/15 bg-white/5"
    }`} data-testid={testid}>
      <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${
        gold ? "bg-[var(--gm-gold)] text-[var(--ink)]"
             : "border border-white/30 text-white/70"
      }`}>
        {gold ? <ShieldCheck size={12} /> : <Sparkles size={12} />}
        {title}{labelSuffix ? ` · ${labelSuffix}` : ""}
      </div>
      <ul className="mt-4 space-y-2.5">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm text-white/85">
            <span className={`mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0 ${gold ? "bg-[var(--gm-gold)]" : "bg-white/40"}`} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// SIGNUP variant — split into two pieces so Register.jsx can insert
// the registration form between the "above-form" and "below-form" content.
// ═════════════════════════════════════════════════════════════════════

/**
 * Above-form:
 *   1. Headline "Build Your Collector Identity"
 *   2. "One Profile for Your Entire World Cup Collection"
 *   3. Short supporting paragraph
 *   4. "Collector Network In Development" badge
 *   5. Large collector profile preview
 *   6. Compact benefit grid (max 6 cards)
 */
export function FutureNetworkSignupAboveForm() {
  return (
    <section className="space-y-6 md:space-y-8" data-testid="signup-future-above">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 items-start">
        {/* LEFT: intro + compact 6-card grid */}
        <div className="space-y-6">
          <div>
            <div className="kicker kicker-red">Build Your Collector Identity</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-[2.6rem] mt-3 leading-tight">
              One Profile for Your Entire<br className="hidden md:inline" /> World Cup Collection.
            </h2>
            <p className="text-[var(--smoke)] mt-4 leading-relaxed text-sm md:text-base">
              Your collector account begins with certificates today, and will
              expand into a complete profile for every World Cup album you own —
              previous tournaments and the upcoming USA / Canada / Mexico 2026
              edition — as new tools are released.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-yellow)] px-4 py-1.5 text-xs font-bold uppercase tracking-widest">
              <Sparkles size={14} /> Collector Network In Development
            </div>
          </div>
          <FeatureIconGrid compact />
        </div>

        {/* RIGHT: profile preview */}
        <div>
          <CollectorProfilePreview />
        </div>
      </div>
    </section>
  );
}

/**
 * Below-form:
 *   9. Expandable detailed feature groups
 *   10. Future trade preview
 *   11. Collector badges preview
 *   12. Nearby collectors preview
 */
export function FutureNetworkSignupBelowForm() {
  return (
    <section className="space-y-10 mt-4" data-testid="signup-future-below">
      {/* Expandable detailed feature groups */}
      <div className="space-y-4" data-testid="signup-benefit-groups">
        <h3 className="font-display text-2xl md:text-3xl leading-tight">
          Explore every feature in development.
        </h3>
        {SIGNUP_GROUPS.map((g, i) => (
          <details
            key={g.title}
            className="rounded-2xl border-2 border-[var(--ink)] bg-white overflow-hidden"
            data-testid={`signup-group-${i + 1}`}
            open={i === 0}
          >
            <summary className="flex items-center justify-between gap-3 cursor-pointer list-none px-5 py-4 hover:bg-[var(--panini-yellow-soft)]/50">
              <div className="flex items-center gap-3">
                <span className="font-mono text-[var(--panini-red)] text-xs">0{i + 1}</span>
                <span className="font-display text-base md:text-lg">{g.title}</span>
              </div>
              <span className="rounded-full bg-[var(--ink)] text-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                Coming Soon
              </span>
            </summary>
            <ul className="px-5 pb-5 space-y-2 border-t border-[var(--ink)]/10 pt-3">
              {g.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-[var(--smoke)]">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[var(--panini-yellow)] flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </details>
        ))}
      </div>

      {/* Trade matching preview */}
      <div className="rounded-3xl border-4 border-[var(--ink)] bg-[var(--field-green)] p-6 md:p-8 shadow-[8px_8px_0_var(--panini-yellow)] text-white" data-testid="signup-matching-preview">
        <div className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
          <Sparkles size={12} /> Future Trade Matching Preview · Concept Preview Only
        </div>
        <h3 className="font-display text-xl md:text-2xl mt-4 leading-tight">
          Imagine Knowing Exactly Who Can Help You.
        </h3>
        <div className="mt-5 space-y-2">
          {TRADE_EXAMPLES.map((line, i) => (
            <div key={i} className="rounded-xl border-2 border-white/30 bg-white/10 px-4 py-2.5 text-sm flex items-start gap-2">
              <GitCompareArrows size={14} className="mt-0.5 flex-shrink-0 text-[var(--panini-yellow)]" />
              <span>{line}</span>
            </div>
          ))}
        </div>
        <p className="text-white/70 mt-4 text-xs leading-relaxed">
          Instead of searching through hundreds of posts, the future platform
          will compare collector lists and highlight possible trade matches
          automatically. <strong>Not connected to backend data.</strong>
        </p>
      </div>

      {/* Badges & tiers preview */}
      <div className="rounded-3xl border-2 border-[var(--ink)] bg-white p-6 md:p-8" data-testid="signup-badges-preview">
        <div className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-yellow)] px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
          <Award size={12} /> Badge and Tier System In Development
        </div>
        <h3 className="font-display text-xl md:text-2xl mt-4">Build Your Collector Legacy.</h3>
        <p className="text-[var(--smoke)] mt-3 text-sm leading-relaxed">
          Collectors will eventually receive badges for owning albums from
          previous World Cups, for completing tournaments, and for reaching
          collection milestones — progressing through collector tiers as their
          collection grows.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5" data-testid="signup-badges-grid">
          {["Previous World Cups Badge", "Completion Badge", "Tournament Badge", "Collector Tier"].map((label) => (
            <div key={label}
                 className="rounded-xl border-2 border-[var(--ink)]/15 bg-[var(--panini-yellow-soft)]/40 p-3 text-center"
                 data-testid={`signup-badge-${label.toLowerCase().replace(/\s+/g, '-')}`}>
              <Award size={22} className="mx-auto text-[var(--panini-red)]" />
              <div className="text-xs mt-2 font-bold">{label}</div>
              <div className="text-[9px] text-[var(--smoke)] uppercase tracking-widest mt-1">Placeholder</div>
            </div>
          ))}
        </div>
      </div>

      {/* Nearby collectors preview */}
      <div className="rounded-2xl border border-[var(--ink)]/15 bg-white/60 p-5 text-sm text-[var(--smoke)] flex items-center gap-3" data-testid="signup-nearby-preview">
        <MapPin size={20} className="text-[var(--panini-red)] flex-shrink-0" />
        <span>
          You will eventually be able to <strong className="text-[var(--ink)]">find collectors near you</strong> and coordinate local pickups.
          <span className="ml-2 rounded-full bg-[var(--ink)] text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">Planned Feature</span>
        </span>
      </div>
    </section>
  );
}

// Backwards-compatible export retained in case any other page references it.
export function FutureNetworkSignup() {
  return (
    <>
      <FutureNetworkSignupAboveForm />
      <FutureNetworkSignupBelowForm />
    </>
  );
}
